import { useCallback, useEffect, useRef, useState } from "react";

const DASHBOARD_VIEW_META_KEY = "dashboard_view_mode_v1";

function getDashboardViewStorageKey(userId) {
  return userId ? `ed_dashboard_view_v1_${userId}` : "ed_dashboard_view_v1_local";
}

function getDashboardViewTipKey(userId) {
  return userId ? `ed_dashboard_view_tip_v1_${userId}` : "ed_dashboard_view_tip_v1_local";
}

function reportNonFatal(scope, error) {
  const message =
    error && typeof error === "object" && "message" in error
      ? String(error.message)
      : String(error ?? "unknown");
  console.warn(`[ExpiryDesk][${scope}] ${message}`);
}

async function persistDashboardViewMetadata(supabaseClient, userMetadata, dashboardView) {
  const { data: latest } = await supabaseClient.auth.getUser();
  const latestMetadata = latest?.user?.user_metadata || {};
  const { error } = await supabaseClient.auth.updateUser({
    data: { ...latestMetadata, ...(userMetadata || {}), [DASHBOARD_VIEW_META_KEY]: dashboardView },
  });
  if (error) throw error;
}

/**
 * Dashboard Rands vs No.'s preference. Local toggle wins over stale session metadata refreshes
 * so the view does not flicker when Supabase auth metadata lags behind updateUser.
 */
export function useDashboardPreferences({ userId, userMetadata, supabaseClient }) {
  const [dashboardView, setDashboardViewState] = useState("finance");
  const [viewModeTipOpen, setViewModeTipOpen] = useState(false);
  const userChoseViewRef = useRef(false);

  const setDashboardView = useCallback((next) => {
    userChoseViewRef.current = true;
    setDashboardViewState(next);
  }, []);

  useEffect(() => {
    userChoseViewRef.current = false;
  }, [userId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (userChoseViewRef.current) return;

    const fromMeta = userMetadata?.[DASHBOARD_VIEW_META_KEY];
    if (fromMeta === "stock" || fromMeta === "finance") {
      setDashboardViewState(fromMeta);
      return;
    }

    const userKey = getDashboardViewStorageKey(userId);
    let raw = window.localStorage.getItem(userKey);
    if (!raw && userId) {
      const anon = window.localStorage.getItem("ed_dashboard_view_v1_local");
      if (anon === "stock" || anon === "finance") {
        raw = anon;
        window.localStorage.setItem(userKey, anon);
      }
    }
    if (raw === "stock" || raw === "finance") setDashboardViewState(raw);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally scoped to stable view key only
  }, [userId, userMetadata?.[DASHBOARD_VIEW_META_KEY]]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const tipKey = getDashboardViewTipKey(userId);
    const seen = window.localStorage.getItem(tipKey) === "1";
    setViewModeTipOpen(!seen);
  }, [userId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const key = getDashboardViewStorageKey(userId);
    window.localStorage.setItem(key, dashboardView);
  }, [dashboardView, userId]);

  useEffect(() => {
    if (!supabaseClient || !userId) return;
    const current = userMetadata?.[DASHBOARD_VIEW_META_KEY];
    if (current === dashboardView) return;
    void (async () => {
      try {
        await persistDashboardViewMetadata(supabaseClient, userMetadata, dashboardView);
      } catch (err) {
        reportNonFatal("persist_dashboard_view", err);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardView, supabaseClient, userId, userMetadata?.[DASHBOARD_VIEW_META_KEY]]);

  function closeViewModeTip() {
    setViewModeTipOpen(false);
    if (typeof window === "undefined") return;
    const tipKey = getDashboardViewTipKey(userId);
    window.localStorage.setItem(tipKey, "1");
  }

  return {
    dashboardView,
    setDashboardView,
    viewModeTipOpen,
    closeViewModeTip,
  };
}
